import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, reactive, ref, type Ref } from 'vue';
import CModal from '@/components/CModal.vue';
import CButtonGroup, { type CButtonGroupItem } from '@/components/shared/CButtonGroup';
import CFileInput, { type CFileInputRef } from '@/components/shared/CFileInput';
import CPagination from '@/components/shared/CPagination';
import CPreview from '@/components/shared/CPreview.vue';
import { getDefaultMasks, getUserMasks } from '@/api/masks';
import type { MaskDto } from '@/api/typings/mask';
import {
    MASK_UPLOAD_ACCEPT,
    RASTER_MASK_UPLOAD_ACCEPT,
    isSvgMask,
    uploadAndRegisterMask
} from '@/utils/maskUtils';

type MaskSource = 'customily' | 'uploaded';


export default /*#__PURE__*/_defineComponent({
  __name: 'MaskSelectorModal',
  props: {
    disableSvg: { type: Boolean }
  },
  emits: ["select", "cancel", "error"],
  setup(__props: any, { emit }: { emit: ({
    (event: 'select', mask: MaskDto): void;
    (event: 'cancel'): void;
    (event: 'error', message: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    disableSvg?: boolean;
};

const PAGE_SIZE = 35;





const sources: CButtonGroupItem<MaskSource>[] = [
    { label: 'Customily', value: 'customily' },
    { label: 'Uploaded', value: 'uploaded' }
];

const model = reactive({
    filters: [] as MaskSource[],
    defaultMasks: [] as MaskDto[],
    userMasks: [] as MaskDto[],
    userMasksTotal: 0,
    page: 1,
    loading: false,
    uploading: false
});

const fileInputRef: Ref<CFileInputRef | undefined> = ref();

const accept = computed(() => (props.disableSvg ? RASTER_MASK_UPLOAD_ACCEPT : MASK_UPLOAD_ACCEPT));
// Filters are inclusive, so none picked and both picked mean the same thing: show everything
const isFiltered = (source: MaskSource) => !model.filters.length || model.filters.includes(source);
const showUserMasks = computed(() => isFiltered('uploaded'));
const showDefaultMasks = computed(() => isFiltered('customily'));

const userMasksCount = computed(() => (showUserMasks.value ? model.userMasksTotal : 0));
const totalMaskCount = computed(
    () => userMasksCount.value + (showDefaultMasks.value ? model.defaultMasks.length : 0)
);

// Unfiltered it is one flat list of the user's masks followed by the Customily ones: the defaults
// start filling the page where the uploaded ones run out and spill onto the next ones
const masks = computed(() => {
    const userMasks = showUserMasks.value ? model.userMasks : [];
    if (!showDefaultMasks.value) return userMasks;

    const firstDefault = Math.max(0, (model.page - 1) * PAGE_SIZE - userMasksCount.value);
    return [
        ...userMasks,
        ...model.defaultMasks.slice(firstDefault, firstDefault + PAGE_SIZE - userMasks.length)
    ];
});
const isEmpty = computed(() => !model.loading && !masks.value.length);
const emptyMessage = computed(() => {
    if (!showDefaultMasks.value) {
        return "You haven't uploaded any shape yet. Upload one and it will be available on every template.";
    }
    if (!showUserMasks.value) return 'There are no Customily shapes available yet.';
    return 'There are no shapes available yet. Upload one and it will be available on every template.';
});

const loadDefaultMasks = async () => {
    model.defaultMasks = await getDefaultMasks();
};

const loadUserMasks = async (page: number) => {
    const { masks: userMasks, totalCount } = await getUserMasks(page, PAGE_SIZE);
    model.userMasks = userMasks;
    model.userMasksTotal = totalCount;
    model.page = page;
};

const withLoading = async (loading: Promise<unknown>) => {
    model.loading = true;
    try {
        await loading;
    } finally {
        model.loading = false;
    }
};

const onPageChange = (page: number) => withLoading(loadUserMasks(page));

const onFiltersChange = (filters: MaskSource[]) => {
    model.filters = filters;
    if (model.page !== 1) onPageChange(1);
};

const isDisabled = (mask: MaskDto) => props.disableSvg && isSvgMask(mask.path);

const onMaskSelected = (mask: MaskDto) => {
    if (isDisabled(mask)) return;
    emit('select', mask);
};

const onUploadClicked = () => fileInputRef.value?.openFileUploader();

const onFileUpload = async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    model.uploading = true;
    try {
        const mask = await uploadAndRegisterMask(file);
        emit('select', mask);
    } catch (error) {
        emit('error', (error as Error).message || 'The mask could not be uploaded');
    } finally {
        model.uploading = false;
    }
};

onMounted(() => withLoading(Promise.all([loadDefaultMasks(), loadUserMasks(1)])));

return { __sfc: true,PAGE_SIZE, props, emit, sources, model, fileInputRef, accept, isFiltered, showUserMasks, showDefaultMasks, userMasksCount, totalMaskCount, masks, isEmpty, emptyMessage, loadDefaultMasks, loadUserMasks, withLoading, onPageChange, onFiltersChange, isDisabled, onMaskSelected, onUploadClicked, onFileUpload, CModal, CButtonGroup, CFileInput, CPagination, CPreview }
}

})